import { useQuery } from '@tanstack/react-query'
import { compareAsc } from 'date-fns'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import { type Semester } from '@/types/semester'
import { semester } from '@/utils/zod'

import { findById } from '../utils/find-by-id'

const responseSchema = z.array(semester)

const parseResponse = (response: z.infer<typeof responseSchema>): Semester[] =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    startDate: item.start_date,
    endDate: item.end_date,
    isActive: item.is_active,
    status: item.status,
    schoolYear: item.school_year
  }))

type Params = {
  classId?: string
  studentId?: string
  tutorId?: string
  userId?: string
  hidePastYearsSemesters?: boolean
}

export const semesterQueryOptions = (params: Params) => ({
  queryKey: ['panelPeriodsSemestersList', params],
  staleTime: 60 * 1000,
  queryFn: async () => {
    const semestersResponse = await httpService.panel.panelPeriodsSemestersList(
      {
        page_size: 'max',
        class_id: params.classId,
        student_id: params.studentId,
        tutor_id: params.tutorId,
        user_id: params.userId,
        hide_past_years_semesters: params.hidePastYearsSemesters,
        fetchKeys: {
          id: true,
          name: true,
          start_date: true,
          end_date: true,
          status: true,
          is_active: true,
          school_year: true
        }
      }
    )

    const semesters = parseResponse(
      responseSchema.parse(semestersResponse.results)
    ).sort((a, b) => compareAsc(a.startDate, b.startDate))

    const currentSemesterIndex = semesters?.findIndex(
      semesterItem => semesterItem.isActive
    )
    const currentSemester = semesters[currentSemesterIndex]
    const previousSemester = semesters[currentSemesterIndex - 1]

    const semestersFromCurrentYear =
      semesters?.filter(
        semesterItem => semesterItem.schoolYear === currentSemester?.schoolYear
      ) || []

    const options = semesters?.map(semesterItem => ({
      label: semesterItem.name,
      value: semesterItem.id
    }))

    return {
      list: semesters,
      options,
      previousSemester,
      currentSemester: currentSemester || semesters[semesters.length - 1],
      semestersFromCurrentYear,
      semestersFromCurrentYearOptions: semestersFromCurrentYear.map(
        semesterItem => ({
          label: semesterItem.name,
          value: semesterItem.id
        })
      ),
      getSemester: findById(semesters)
    }
  }
})

const useSemesters = (params: Params = {}) =>
  useQuery(semesterQueryOptions(params))

export default useSemesters
